import { useEffect, useState } from "react";
import useApi from "./useApi";
import { toast } from "react-toastify";
import { useUser } from "./useUser";
import useFileUploader from "./useFileUploader";

const roleConfig = {
  admin: {
    fetch: "me",
    update: "admin/profile",
    image: "admin/profile-image",
    changePassword: null,
    fields: ["name", "email", "phone_no"],
  },
  employee: {
    fetch: "employee/profile",
    update: "employee/profile",
    image: null,
    changePassword: null,
    fields: ["name", "email", "phone_no"],
  },
  vendor: {
    fetch: "vendor/profile",
    update: "vendor/profile",
    image: null,
    changePassword: null,
    fields: ["name", "email", "phone_no", "longitude", "latitude"],
  },
  driver: {
    fetch: "delivery/profile",
    update: "delivery/profile",
    image: null,
    changePassword: null,
    fields: ["name", "email", "phone_no"],
  },
};

export default function useProfileEdit(role = "admin") {
  const { get, put, post } = useApi();
  const config = roleConfig[role] || roleConfig.admin;

  // Only keep fields in state that are in config.fields
  const initialProfile = config.fields.reduce((acc, field) => {
    acc[field] = "";
    return acc;
  }, {});

  const { data: user, isLoading: userLoading, error: userError } = useUser();
  const { uploadImage, uploadStatus } = useFileUploader();

  const [profile, setProfile] = useState(initialProfile);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [loading, setLoading] = useState(false);

  // Initialize profile state from user data
  useEffect(() => {
    if (user) {
      const newProfile = { ...initialProfile };
      config.fields.forEach((field) => {
        newProfile[field] = user[field] || "";
      });
      setProfile(newProfile);
      if (user.profile_image) {
        setProfileImageUrl(user.profile_image);
      }
    }
    // eslint-disable-next-line
  }, [user, role]);

  useEffect(() => {
    if (userError) {
      toast.error("Failed to load profile");
    }
  }, [userError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
    }
  };

  const updateProfileMutation = put(config.update, {
    onSuccess: () => {
      // Invalidate user query to refetch updated data
      const queryClient = updateProfileMutation.queryClient || updateProfileMutation._queryClient;
      if (queryClient) {
        queryClient.invalidateQueries(['user']);
        queryClient.invalidateQueries(['me']);
      }
    }
  });

  // Create image update mutation
  const updateImageMutation = post(config.image || 'admin/profile-image', {
    onSuccess: () => {
      // Invalidate user queries when image is updated
      const queryClient = updateProfileMutation.queryClient || updateProfileMutation._queryClient;
      if (queryClient) {
        queryClient.invalidateQueries(['user']);
        queryClient.invalidateQueries(['me']);
      }
    }
  });

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await updateProfileMutation.mutateAsync({ id: '', payload: profile });
      if (res.status === "success") {
        toast.success("Profile updated successfully");
        // Force refetch user data to update local state
        window.location.reload();
      } else if (res.errors) {
        Object.values(res.errors).flat().forEach(msg => toast.error(msg));
      } else {
        toast.error(res.message || "Failed to update profile");
      }
    } catch (e) {
      const errors = e?.response?.data?.errors;
      if (errors) {
        Object.values(errors).flat().forEach(msg => toast.error(msg));
      } else {
        toast.error(e?.response?.data?.message || "Failed to update profile");
      }
    } finally {
      setLoading(false);
    }
  };

  // Profile image upload (if supported)
  const handleImageSubmit = async (e) => {
    e.preventDefault();
    if (!profileImage) {
      toast.error("Please select an image");
      return;
    }
    setLoading(true);
    try {
      // Step 1: Upload image to server
      const filename = user ? `admin_${user.user_id}` : "admin_profile";
      const uploadedUrl = await uploadImage(profileImage, "profile_images", filename);
      
      if (!uploadedUrl) {
        toast.error("Failed to upload image");
        return;
      }

      // Step 2: Update profile with new image URL
      const imageEndpoint = config.image || 'admin/profile-image';
      const updateRes = await updateImageMutation.mutateAsync({ profile_image: uploadedUrl });
      
      if (updateRes.status === "success") {
        setProfileImageUrl(uploadedUrl);
        toast.success("Profile image updated successfully");
        
        // Reset file input
        setProfileImage(null);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        
        // Force page reload to ensure all components update
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.error(updateRes.message || "Failed to save image to profile");
      }
    } catch (e) {
      console.error('Image upload error:', e);
      const errorMessage = e?.response?.data?.message || e?.message || "Failed to update image";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    profile,
    setProfile,
    handleChange,
    profileImage,
    setProfileImage,
    profileImageUrl,
    setProfileImageUrl,
    handleImageChange,
    handleImageSubmit,
    handleProfileSubmit,
    loading: loading || userLoading,
    fields: config.fields,
    role,
  };
} 